import {
  CorrespondentStats,
  AddInvestorRequest,
  getCorrespondentStats,
  InvestorProfile,
  removeInvestor,
} from '@/api/custom';
import {
  DeleteOutlined,
  DownloadOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { getInvestorNameOrFallback, UNNAMED_INVESTOR_LABEL } from '@/util/investor-display';
import {
  Alert,
  Button,
  Col,
  Dropdown,
  Flex,
  Input,
  message,
  Modal,
  Progress,
  Row,
  Tabs,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './index.scss';
import { captureException } from '@/sentry';
import { fetchInvestorsAllThunk } from '@/store/features/investors/investorsThunk';
import { buildInvestorsContextKey } from '@/store/features/investors/investorsContext';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  investorRemoved,
  selectInvestors,
  selectInvestorsLoadingState,
  selectInvestorsNeedsFetch,
} from '@/store/features/investors/investorsSlice';
import { selectProfileData, selectUser } from '@/store/features/userinfo/userinfoSlice';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  buildPageLoaderBranding,
  buildInitialAppLoadPageLoaderMessages,
  CLIENTS_DESTINATION_PAGE_LOADER_MESSAGES,
  CLIENTS_PAGE_LOADER_MESSAGES,
  getAdvisorNameFromUser,
  WavvestPageLoader,
} from '@/common/components/page-loader';
import {
  clearInitialAppLoadState,
  getInitialAppLoadAdvisorName,
  getInitialAppLoadBranding,
  getInitialAppLoadStartedAt,
  getInitialAppLoadSuppressAdvisorName,
  hasInitialAppLoadState,
} from '@/routes/initial-app-load-state';
import { useFirmDataEnabled } from '@/hooks/useFirmDataEnabled';
import { Text } from '@/common/components/themed';
import { AdvisorRouteSkeleton } from '@/routes/components/route-loading-skeleton';
import { createInvestorWithArchivedRestore } from '@/common/components/investor-profile-form/create-investor-archived-restore';
import {
  ALL_CLIENT_LIFECYCLE_FILTER,
  CLIENT_LIFECYCLE_STAGE_FILTERS,
  buildClientRosterRows,
  csvCell,
  filterClientRosterRows,
  formatCompactCurrency,
  type ClientLifecycleFilter,
  type ClientRosterRow,
} from './book-of-business';
import { investorCopy } from './investorCopy';
import {
  buildDeleteInvestorSupportContext,
  buildDeleteInvestorSupportError,
  canContactTeamForDeleteInvestorFailure,
  canRefreshListForDeleteInvestorFailure,
  canRetryDeleteInvestorFailure,
  getDeleteInvestorFailureKind,
} from './delete-investor-recovery';
import type { DeleteInvestorFailureKind } from './delete-investor-recovery';
import {
  CreateContactRequest,
  createWealthboxContacts,
  createWealthboxInvestorContactRelationship,
  getInitiateWealthboxOauthUrl,
  getWealthboxContacts,
  getWealthboxInvestorContactRelationships,
  getWealthboxStatus,
} from '@/api/wealthbox';

type WealthboxContact = {
  id: number;
  first_name: string;
  last_name: string;
  email_addresses: Array<{
    address: string;
  }>;
  phone_numbers: Array<{
    address: string;
  }>;
};

type WealthboxMergeCandidate = {
  ourId: string;
  wbId: number;
  investor: InvestorProfile;
};

type WealthboxSyncState = {
  toImport: WealthboxContact[];
  toExport: InvestorProfile[];
  toMerge: WealthboxMergeCandidate[];
};

type WealthboxCreatedContact = {
  id: number | string;
};

type WealthboxSyncFailure = {
  item: string;
  operation: string;
  reason: string;
};

type WealthboxAuthorizationStatus = 'authorized' | 'not_authorized' | 'error';

type DeleteInvestorFailure = {
  error: unknown;
  kind: DeleteInvestorFailureKind;
  retryAttempted: boolean;
};

type DeleteSupportStatus = 'idle' | 'notifying' | 'notified';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getApiErrorDetail = (error: unknown): unknown => {
  if (!isRecord(error)) return undefined;
  const response = error.response;
  if (!isRecord(response)) return undefined;
  const data = response.data;
  if (!isRecord(data)) return undefined;
  return data.detail;
};

const getErrorMessage = (error: unknown) => {
  const detail = getApiErrorDetail(error);
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (isRecord(detail) && typeof detail.message === 'string') {
    return detail.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
};

const formatWealthboxContactName = (contact: WealthboxContact) =>
  `${contact.first_name} ${contact.last_name}`.trim() || `Wealthbox contact ${contact.id}`;

const formatWealthboxFailureCount = (count: number) =>
  `${count} failed ${count === 1 ? 'item' : 'items'}`;

const getClientsInitialLoadCheckpointMessages = ({
  loadingClients,
  loadingStats,
}: {
  loadingClients: boolean;
  loadingStats: boolean;
}): ReadonlyArray<string> => {
  if (loadingClients) {
    return CLIENTS_DESTINATION_PAGE_LOADER_MESSAGES.slice(0, 1);
  }

  if (loadingStats) {
    return CLIENTS_DESTINATION_PAGE_LOADER_MESSAGES;
  }

  return CLIENTS_DESTINATION_PAGE_LOADER_MESSAGES;
};

const UNNAMED_CLIENT_LABEL = 'Unnamed client';
const LIFECYCLE_FILTER_DRAG_THRESHOLD = 3;

const getClientNameOrFallback = (name?: string | null) => {
  const displayName = getInvestorNameOrFallback(name);
  return displayName === UNNAMED_INVESTOR_LABEL ? UNNAMED_CLIENT_LABEL : displayName;
};

const getClientDisplayName = (investor: Pick<InvestorProfile, 'name'>) =>
  getClientNameOrFallback(investor.name);

const Investors: React.FC = () => {
  const user = useAppSelector(selectUser);
  const profileData = useAppSelector(selectProfileData);
  const investorsContextKey = useMemo(() => buildInvestorsContextKey(user), [user]);
  const investorsNeedFetch = useAppSelector(selectInvestorsNeedsFetch);
  const investorsLoadingState = useAppSelector(selectInvestorsLoadingState);
  const loadingData = investorsLoadingState === 'pending' || investorsNeedFetch;
  const investorsLoadFailed = investorsLoadingState === 'failed';
  const investorData = useAppSelector(selectInvestors);
  const totalInvestors = investorData.length;
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<ClientLifecycleFilter>(
    ALL_CLIENT_LIFECYCLE_FILTER,
  );
  const lifecycleFilterOptionsRef = useRef<HTMLDivElement | null>(null);
  const lifecycleFilterDragRef = useRef({
    pointerId: null as number | null,
    startX: 0,
    scrollLeft: 0,
    moved: false,
    suppressNextClick: false,
  });
  const [isLifecycleFilterDragging, setIsLifecycleFilterDragging] = useState(false);
  const [stats, setStats] = useState<CorrespondentStats>({
    aum: '0',
    total_fee: '0',
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsLoadFailed, setStatsLoadFailed] = useState(false);
  const newInvestors = useMemo(
    () =>
      investorData.filter((investor) => new Date(investor.created_at).getFullYear() === currentYear)
        .length,
    [currentYear, investorData],
  );
  const dispatch = useAppDispatch();
  const [deletionReason, setDeletionReason] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [investorToDelete, setInvestorToDelete] = useState<{
    id: string;
    displayName: string;
  } | null>(null);
  const [deletingInvestor, setDeletingInvestor] = useState<boolean>(false);
  const [deleteFailure, setDeleteFailure] = useState<DeleteInvestorFailure | null>(null);
  const [deleteSupportStatus, setDeleteSupportStatus] = useState<DeleteSupportStatus>('idle');
  const [deleteSupportError, setDeleteSupportError] = useState('');
  const { loading: firmDataLoading, error: firmDataError } = useFirmDataEnabled();

  const navigate = useNavigate();
  const location = useLocation();
  const isInvestorDetailPage =
    location.pathname !== '/home/clients' && location.pathname.startsWith('/home/clients/');
  const locationHasInitialAppLoad = hasInitialAppLoadState(location.state);
  const [isInitialClientsLoad] = useState(() => locationHasInitialAppLoad);
  const [initialClientsLoaderStartedAt] = useState(() =>
    getInitialAppLoadStartedAt(location.state),
  );
  const [initialClientsAdvisorName] = useState(() => getInitialAppLoadAdvisorName(location.state));
  const [initialClientsBranding] = useState(() => getInitialAppLoadBranding(location.state));
  const [suppressInitialClientsAdvisorName] = useState(() =>
    getInitialAppLoadSuppressAdvisorName(location.state),
  );
  const [showInitialClientsLoader, setShowInitialClientsLoader] =
    useState<boolean>(isInitialClientsLoad);
  const isClientsInitialDataLoading = loadingData || statsLoading || firmDataLoading;
  const initialClientsLoaderBranding = useMemo(
    () => buildPageLoaderBranding(profileData) ?? initialClientsBranding,
    [initialClientsBranding, profileData],
  );
  const initialClientsLoaderMessages = useMemo(
    () =>
      isInitialClientsLoad
        ? buildInitialAppLoadPageLoaderMessages({
            advisorName: suppressInitialClientsAdvisorName
              ? undefined
              : (initialClientsAdvisorName ??
                getInitialAppLoadAdvisorName(location.state) ??
                getAdvisorNameFromUser(user)),
            destination: 'clients',
            destinationMessages: getClientsInitialLoadCheckpointMessages({
              loadingClients: loadingData,
              loadingStats: statsLoading,
            }),
          })
        : CLIENTS_PAGE_LOADER_MESSAGES,
    [
      initialClientsAdvisorName,
      isInitialClientsLoad,
      loadingData,
      location.state,
      statsLoading,
      suppressInitialClientsAdvisorName,
      user,
    ],
  );

  useEffect(() => {
    if (!locationHasInitialAppLoad) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: clearInitialAppLoadState(location.state),
      },
    );
  }, [
    locationHasInitialAppLoad,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  const handleInitialClientsLoaderHandoffComplete = useCallback(() => {
    setShowInitialClientsLoader(false);
  }, []);

  const clearDeleteFailure = () => {
    setDeleteFailure(null);
    setDeleteSupportStatus('idle');
    setDeleteSupportError('');
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletionReason('');
    setInvestorToDelete(null);
    clearDeleteFailure();
  };

  const handleInvalidDeleteRequest = async (failure: DeleteInvestorFailure) => {
    try {
      await dispatch(fetchInvestorsAllThunk()).unwrap();
      closeDeleteModal();
    } catch (refreshError) {
      console.error('Failed to refresh investors after invalid delete request:', refreshError);
      setDeleteFailure(failure);
      setDeleteSupportStatus('idle');
      setDeleteSupportError(investorCopy.deleteFailure.refreshListFailure);
    }
  };

  const handleDeleteInvestor = async () => {
    if (!investorToDelete || user?.role !== 'admin') return;
    if (!deletionReason.trim()) {
      message.error('Enter a reason for removal.');
      return;
    }

    const retryAttempted = Boolean(deleteFailure);
    const investorBeingDeleted = investorToDelete;
    setDeletingInvestor(true);
    setDeleteSupportError('');
    try {
      await removeInvestor(investorBeingDeleted.id, deletionReason);
      dispatch(investorRemoved(investorBeingDeleted.id));
      await dispatch(fetchInvestorsAllThunk()).unwrap();
      closeDeleteModal();
    } catch (error) {
      const kind = getDeleteInvestorFailureKind(error);
      if (kind === 'already_deleted') {
        await dispatch(fetchInvestorsAllThunk()).unwrap();
        closeDeleteModal();
        return;
      }

      const nextDeleteFailure = {
        error,
        kind,
        retryAttempted,
      };
      if (kind === 'invalid_request') {
        await handleInvalidDeleteRequest(nextDeleteFailure);
        return;
      }

      setDeleteFailure(nextDeleteFailure);
      setDeleteSupportStatus('idle');
      console.error('Error deleting investor:', error);
      if (canContactTeamForDeleteInvestorFailure(nextDeleteFailure)) {
        notifyDeleteSupport(nextDeleteFailure, investorBeingDeleted);
      }
    } finally {
      setDeletingInvestor(false);
    }
  };

  const showInvestorDeleteModal = (investorId: string, investorDisplayName: string) => {
    setInvestorToDelete({ id: investorId, displayName: investorDisplayName });
    clearDeleteFailure();
    setDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    closeDeleteModal();
  };

  const notifyDeleteSupport = (
    failure: DeleteInvestorFailure,
    investor: { id: string; displayName: string },
  ) => {
    setDeleteSupportStatus('notifying');
    setDeleteSupportError('');
    try {
      captureException(
        buildDeleteInvestorSupportError(failure.error),
        buildDeleteInvestorSupportContext({
          error: failure.error,
          investorDisplayName: investor.displayName,
          investorId: investor.id,
          kind: failure.kind,
          retryAttempted: failure.retryAttempted,
        }),
      );
      setDeleteSupportStatus('notified');
    } catch (error) {
      console.error('Failed to notify team about delete investor failure:', error);
      setDeleteSupportError(investorCopy.deleteFailure.supportNotificationFailure);
      setDeleteSupportStatus('idle');
    }
  };

  const handleContactDeleteSupport = () => {
    if (!deleteFailure || !investorToDelete || deleteSupportStatus === 'notified') {
      return;
    }

    notifyDeleteSupport(deleteFailure, investorToDelete);
  };

  const handleRefreshDeleteList = () => {
    window.location.reload();
  };

  const getDeleteFailureBody = () => {
    if (!deleteFailure) {
      return '';
    }

    if (deleteSupportStatus === 'notified') {
      return investorCopy.deleteFailure.notifiedBody;
    }

    switch (deleteFailure.kind) {
      case 'access_denied':
        return investorCopy.deleteFailure.accessDeniedBody;
      case 'invalid_request':
        return investorCopy.deleteFailure.invalidRequestBody;
      case 'signed_up_conflict':
        return investorCopy.deleteFailure.signedUpConflictBody;
      case 'unconfirmed':
        return deleteFailure.retryAttempted
          ? investorCopy.deleteFailure.failedBodyAfterRetry
          : investorCopy.deleteFailure.failedBody;
      case 'already_deleted':
        return '';
    }
  };

  const canRetryDeleteFailure = !deleteFailure || canRetryDeleteInvestorFailure(deleteFailure.kind);
  const canRefreshDeleteList = Boolean(
    deleteFailure && canRefreshListForDeleteInvestorFailure(deleteFailure.kind),
  );
  const hasDeleteRecoveryAction = canRetryDeleteFailure || canRefreshDeleteList;
  const canContactTeamForDeleteFailure =
    deleteFailure &&
    deleteSupportStatus !== 'notified' &&
    canContactTeamForDeleteInvestorFailure(deleteFailure);
  const showDeleteConfirmationDetails = canRetryDeleteFailure;

  const rosterRows = useMemo(
    () => buildClientRosterRows(investorData, getClientDisplayName),
    [investorData],
  );
  const filteredRows = useMemo(
    () => filterClientRosterRows(rosterRows, searchPhrase, lifecycleFilter),
    [lifecycleFilter, rosterRows, searchPhrase],
  );
  const lifecycleCounts = useMemo(() => {
    const counts = new Map<ClientLifecycleFilter, number>([
      [ALL_CLIENT_LIFECYCLE_FILTER, rosterRows.length],
    ]);
    CLIENT_LIFECYCLE_STAGE_FILTERS.forEach((stage) => counts.set(stage, 0));
    rosterRows.forEach((row) => {
      counts.set(row.stage, (counts.get(row.stage) ?? 0) + 1);
    });
    return counts;
  }, [rosterRows]);
  const parsedAum = Number.parseFloat(stats.aum);
  const aumValue = Number.isFinite(parsedAum) ? parsedAum : 0;

  useEffect(() => {
    if (isInvestorDetailPage) {
      return;
    }

    dispatch(fetchInvestorsAllThunk({ contextKey: investorsContextKey }));
  }, [dispatch, investorsContextKey, isInvestorDetailPage]);

  useEffect(() => {
    if (isInvestorDetailPage) {
      return undefined;
    }

    let cancelled = false;

    async function loadStats() {
      setStatsLoading(true);
      setStatsLoadFailed(false);
      try {
        const stats = await getCorrespondentStats();
        if (!cancelled) {
          setStats(stats);
        }
      } catch (error) {
        if (!cancelled) {
          setStatsLoadFailed(true);
        }
        console.error('Failed to load correspondent stats:', error);
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      cancelled = true;
    };
  }, [isInvestorDetailPage]);

  const handleAddNewInvestor = () => {
    if (firmDataLoading) {
      return;
    }

    // If the firm-data flag couldn't be resolved, do NOT fall back to the legacy
    // create flow; that would create a client through the non-canonical path for
    // a firm that may actually be firm-data. Block and let the user retry.
    if (firmDataError) {
      message.error('Could not verify your firm configuration. Please try again.');
      return;
    }

    navigate('new', {
      state: { isNewInvestor: true },
    });
  };

  const handleExportClients = () => {
    const rows = filteredRows.map((row) =>
      [
        csvCell(row.displayName),
        csvCell(row.lifecycleStatus),
        csvCell(row.investor.email),
        csvCell(row.investor.phone_number),
      ].join(','),
    );
    const csv = [
      ['Name', 'Lifecycle status', 'Email', 'Phone'].map(csvCell).join(','),
      ...rows,
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
  };

  const handleLifecycleFilterWheel = useCallback((event: ReactWheelEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (target.scrollWidth <= target.clientWidth) {
      return;
    }

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) {
      return;
    }

    const previousScrollLeft = target.scrollLeft;
    target.scrollLeft += delta;

    if (target.scrollLeft !== previousScrollLeft) {
      event.preventDefault();
    }
  }, []);

  const handleLifecycleFilterPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }
      if (target.scrollWidth <= target.clientWidth) {
        return;
      }
      if (!Number.isFinite(event.clientX)) {
        return;
      }

      lifecycleFilterDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        scrollLeft: target.scrollLeft,
        moved: false,
        suppressNextClick: false,
      };
    },
    [],
  );

  const handleLifecycleFilterPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const dragState = lifecycleFilterDragRef.current;
      if (dragState.pointerId !== event.pointerId) {
        return;
      }
      if (!Number.isFinite(event.clientX)) {
        return;
      }

      const deltaX = event.clientX - dragState.startX;
      if (Math.abs(deltaX) > LIFECYCLE_FILTER_DRAG_THRESHOLD) {
        if (!dragState.moved) {
          dragState.moved = true;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          setIsLifecycleFilterDragging(true);
        }
      }

      if (!dragState.moved) {
        return;
      }

      event.currentTarget.scrollLeft = dragState.scrollLeft - deltaX;
      event.preventDefault();
    },
    [],
  );

  const endLifecycleFilterDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const dragState = lifecycleFilterDragRef.current;
    if (dragState.pointerId !== event.pointerId) {
      return;
    }

    if (dragState.moved) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    dragState.suppressNextClick = dragState.moved;
    dragState.pointerId = null;
    dragState.moved = false;
    if (dragState.suppressNextClick) {
      window.setTimeout(() => {
        lifecycleFilterDragRef.current.suppressNextClick = false;
      }, 0);
    }
    setIsLifecycleFilterDragging(false);
  }, []);

  const cancelLifecycleFilterDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const dragState = lifecycleFilterDragRef.current;
    if (dragState.pointerId !== event.pointerId) {
      return;
    }

    if (dragState.moved) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    dragState.pointerId = null;
    dragState.moved = false;
    dragState.suppressNextClick = false;
    setIsLifecycleFilterDragging(false);
  }, []);

  const handleLifecycleFilterClickCapture = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!lifecycleFilterDragRef.current.suppressNextClick) {
        return;
      }

      lifecycleFilterDragRef.current.suppressNextClick = false;
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  const handleLifecycleFilterClick = useCallback((nextFilter: ClientLifecycleFilter) => {
    setLifecycleFilter(nextFilter);
  }, []);

  const wealthboxEnabled = useAppSelector(
    (state) => state.userinfo.deploymentFlags.wealthboxEnabled === true,
  );

  if (isInvestorDetailPage) {
    return <Outlet />;
  }

  const initialClientsLoader = showInitialClientsLoader ? (
    <WavvestPageLoader
      animationStartedAt={initialClientsLoaderStartedAt}
      branding={initialClientsLoaderBranding}
      destination="clients"
      isReady={!isClientsInitialDataLoading}
      messages={initialClientsLoaderMessages}
      onHandoffComplete={handleInitialClientsLoaderHandoffComplete}
      showFallbackBranding={!initialClientsLoaderBranding}
    />
  ) : null;

  if (showInitialClientsLoader && isClientsInitialDataLoading) {
    return initialClientsLoader;
  }

  if (loadingData) {
    return <AdvisorRouteSkeleton pathname="/home/clients" />;
  }

  return (
    <>
      <div id="crm-page-content" className="book-of-business-page">
        <header className="book-head">
          <div className="book-head-main">
            <h1 className="book-title">Clients</h1>
          </div>

          <div className="book-head-actions">
            <Button
              aria-label="Export"
              className="book-button"
              icon={<DownloadOutlined />}
              onClick={handleExportClients}
              disabled={filteredRows.length === 0}
            >
              Export
            </Button>
            <Button
              aria-label="Add client"
              className="book-button book-button-primary"
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNewInvestor}
              loading={firmDataLoading}
            >
              Add client
            </Button>
            {wealthboxEnabled && <WealthboxButton />}
          </div>
          <p className="book-summary">Your book of business</p>
        </header>

        <section className="book-overview" aria-label="Book overview">
          <div className="book-figure-grid">
            <div className="book-figure-cell">
              <span className="book-figure-label">Clients</span>
              <span className="book-figure-value">{loadingData ? '...' : totalInvestors}</span>
              <span className="book-figure-helper">
                {newInvestors} added in {currentYear}
              </span>
            </div>
            <div className="book-figure-cell" aria-busy={statsLoading}>
              <span className="book-figure-label">Assets under management</span>
              <span className="book-figure-value">
                {statsLoading
                  ? '...'
                  : statsLoadFailed
                    ? 'Unavailable'
                    : formatCompactCurrency(aumValue)}
              </span>
              {statsLoadFailed ? (
                <span className="book-figure-helper">Refresh to retry firm AUM</span>
              ) : null}
            </div>
          </div>
        </section>

        <div className="book-toolbar">
          <label className="book-search">
            <SearchOutlined aria-hidden="true" />
            <Input
              variant="borderless"
              placeholder="Search by name or contact info"
              aria-label="Search clients"
              value={searchPhrase}
              onChange={(event) => setSearchPhrase(event.target.value)}
            />
          </label>
        </div>

        <div className="book-filter-row" aria-label="Lifecycle filters">
          <span className="book-eyebrow">Lifecycle</span>
          <div
            ref={lifecycleFilterOptionsRef}
            className={`book-filter-options${isLifecycleFilterDragging ? ' is-dragging' : ''}`}
            role="group"
            aria-label="Lifecycle options"
            onPointerDown={handleLifecycleFilterPointerDown}
            onPointerMove={handleLifecycleFilterPointerMove}
            onPointerUp={endLifecycleFilterDrag}
            onPointerCancel={cancelLifecycleFilterDrag}
            onClickCapture={handleLifecycleFilterClickCapture}
            onWheel={handleLifecycleFilterWheel}
          >
            <button
              className="book-filter-chip"
              type="button"
              aria-pressed={lifecycleFilter === ALL_CLIENT_LIFECYCLE_FILTER}
              onClick={() => handleLifecycleFilterClick(ALL_CLIENT_LIFECYCLE_FILTER)}
            >
              All <span className="book-filter-count">{lifecycleCounts.get('all') ?? 0}</span>
            </button>
            {CLIENT_LIFECYCLE_STAGE_FILTERS.map((stage) => (
              <button
                className="book-filter-chip"
                key={stage}
                type="button"
                aria-pressed={lifecycleFilter === stage}
                onClick={() => handleLifecycleFilterClick(stage)}
              >
                {stage} <span className="book-filter-count">{lifecycleCounts.get(stage) ?? 0}</span>
              </button>
            ))}
          </div>
        </div>

        <section className="book-roster" aria-label="Client roster">
          <div className="book-roster-head" aria-hidden="true">
            <span>Client</span>
            <span className="book-status-col">Status</span>
            <span className="book-email-col">Email</span>
            <span className="book-phone-col">Phone</span>
            <span></span>
          </div>

          {investorsLoadFailed ? (
            <div className="book-roster-alert">
              <Alert
                showIcon
                type="error"
                message="Clients did not load"
                description="Try again to refresh the book of business."
                action={
                  <Button
                    size="small"
                    onClick={() =>
                      dispatch(fetchInvestorsAllThunk({ contextKey: investorsContextKey }))
                    }
                  >
                    Retry
                  </Button>
                }
              />
            </div>
          ) : filteredRows.length > 0 ? (
            <div className="book-roster-rows">
              {filteredRows.map((row) => (
                <ClientRosterRow
                  key={row.investor.id}
                  row={row}
                  canDelete={user?.role === 'admin'}
                  onDelete={showInvestorDeleteModal}
                />
              ))}
            </div>
          ) : (
            <div className="book-empty-state">
              {searchPhrase || lifecycleFilter !== ALL_CLIENT_LIFECYCLE_FILTER
                ? 'No client matches those filters.'
                : 'No clients yet. Add a client to start building your book of business.'}
            </div>
          )}
        </section>

        <div className="book-roster-foot">
          <span>
            <span className="book-mono">{filteredRows.length}</span> of{' '}
            <span className="book-mono">{totalInvestors}</span> clients
          </span>
        </div>

        <Modal
          title="Delete Client"
          open={deleteModalOpen}
          onCancel={handleCancelDelete}
          footer={[
            <Button key="cancel" onClick={handleCancelDelete}>
              {hasDeleteRecoveryAction ? 'Cancel' : 'Close'}
            </Button>,
            canContactTeamForDeleteFailure ? (
              <Button
                key="contact-team"
                loading={deleteSupportStatus === 'notifying'}
                onClick={handleContactDeleteSupport}
              >
                {investorCopy.deleteFailure.contactTeamButton}
              </Button>
            ) : null,
            canRefreshDeleteList ? (
              <Button key="refresh-list" type="primary" onClick={handleRefreshDeleteList}>
                {investorCopy.deleteFailure.refreshListButton}
              </Button>
            ) : null,
            canRetryDeleteFailure ? (
              <Button
                key="delete"
                className="delete-client-danger-button"
                type="primary"
                danger
                loading={deletingInvestor}
                onClick={handleDeleteInvestor}
              >
                {deleteFailure ? investorCopy.deleteFailure.tryAgainButton : 'Delete'}
              </Button>
            ) : null,
          ]}
          centered
        >
          <div style={{ marginBottom: '16px' }}>
            {deleteFailure ? (
              <Alert
                showIcon
                type={deleteSupportStatus === 'notified' ? 'success' : 'error'}
                message={
                  deleteSupportStatus === 'notified'
                    ? investorCopy.deleteFailure.notifiedTitle
                    : getDeleteFailureBody()
                }
                description={
                  deleteSupportStatus === 'notified'
                    ? getDeleteFailureBody()
                    : deleteSupportError || undefined
                }
                style={{ marginBottom: '16px' }}
              />
            ) : null}
            {showDeleteConfirmationDetails ? (
              <>
                <p>Remove this client from your working view? Their records will be retained.</p>
                <Input.TextArea
                  aria-label="Reason for removal"
                  placeholder="Reason for removal"
                  value={deletionReason}
                  onChange={(event) => setDeletionReason(event.target.value)}
                  maxLength={1000}
                />
                {investorToDelete && (
                  <div
                    style={{
                      padding: '12px',
                      backgroundColor: '#fff2f0',
                      border: '1px solid #ffccc7',
                      borderRadius: '6px',
                      marginTop: '12px',
                    }}
                  >
                    <strong>Client Name:</strong> {investorToDelete.displayName}
                  </div>
                )}
                <p
                  style={{
                    marginTop: '16px',
                    color: '#ff4d4f',
                    fontWeight: '500',
                  }}
                >
                  ⚠️ This action cannot be undone.
                </p>
              </>
            ) : null}
          </div>
        </Modal>
      </div>
      {initialClientsLoader}
    </>
  );
};

type ClientRosterRowProps = {
  row: ClientRosterRow;
  canDelete: boolean;
  onDelete: (investorId: string, investorDisplayName: string) => void;
};

function ClientRosterRow({ row, onDelete, canDelete }: ClientRosterRowProps) {
  const { investor, displayName } = row;
  const email = investor.email?.trim() || '-';
  const phone = investor.phone_number?.trim() || '-';
  const menuItems: MenuProps['items'] = [
    {
      key: 'delete',
      danger: true,
      disabled: !canDelete,
      icon: <DeleteOutlined aria-hidden="true" />,
      label: 'Delete',
      title: !canDelete ? 'Only administrators can remove clients' : undefined,
    },
  ];
  const handleMenuClick: MenuProps['onClick'] = ({ domEvent, key }) => {
    domEvent.stopPropagation();

    if (key === 'delete' && canDelete) {
      onDelete(investor.id, displayName);
    }
  };

  return (
    <div className="book-client-row">
      <Link
        className="book-client-row-link"
        to={`/home/clients/${encodeURIComponent(investor.id)}/profile`}
        state={{ user: investor }}
      >
        <span className="book-who-text">
          <span className="book-who-name" title={displayName}>
            {displayName}
          </span>
          <span className="book-who-sub" title={row.contactLine}>
            {row.contactLine}
          </span>
        </span>
        <span className="book-status-cell" title={row.lifecycleStatus}>
          {row.lifecycleStatus}
        </span>
        <span
          className={`book-email-cell ${email === '-' ? 'is-empty' : ''}`}
          title={email === '-' ? 'No email on file' : email}
        >
          {email}
        </span>
        <span
          className={`book-phone-cell ${phone === '-' ? 'is-empty' : ''}`}
          title={phone === '-' ? 'No phone on file' : phone}
        >
          {phone}
        </span>
      </Link>
      <div className="book-row-actions" aria-label={`Actions for ${displayName}`}>
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            size="small"
            className="app-accent-action-button book-row-menu-button"
            aria-label={`Open actions for ${displayName}`}
            title={`Open actions for ${displayName}`}
            onClick={(event) => event.stopPropagation()}
          />
        </Dropdown>
      </div>
    </div>
  );
}

export default Investors;

function WealthboxMarkIcon() {
  return (
    <svg aria-hidden="true" className="book-wealthbox-icon" focusable="false" viewBox="0 0 18 18">
      <path
        d="M3.25 4.75 5.65 13.25 9 6.35l3.35 6.9 2.4-8.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function WealthboxButton() {
  const taskDispatched = useRef(false); // Ensure only fetch once when mounted

  const investors = useAppSelector(selectInvestors);

  const [wealthboxState, setWealthboxState] = useState<{
    oauthUrl?: string;
    status?: WealthboxAuthorizationStatus;
  }>({});

  const user = useAppSelector(selectUser);

  useEffect(() => {
    const task = async () => {
      try {
        const status = await getWealthboxStatus();
        if (status.status === 'authorized') {
          setWealthboxState({
            status: 'authorized',
          });
        } else {
          const url = await getInitiateWealthboxOauthUrl();
          setWealthboxState({
            status: 'not_authorized',
            oauthUrl: url,
          });
        }
      } catch (error) {
        console.error('Failed to load Wealthbox authorization status:', error);
        setWealthboxState({
          status: 'error',
        });
      }
    };
    if (!taskDispatched.current && user?.role === 'admin') {
      taskDispatched.current = true;
      task();
    }
  }, [user]);

  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncState, setSyncState] = useState<WealthboxSyncState | undefined>(undefined);
  const [syncFailures, setSyncFailures] = useState<WealthboxSyncFailure[]>([]);

  async function startImportingInvestors() {
    setSyncInProgress(true);
    setSyncFailures([]);

    try {
      const wbContacts = await getWealthboxContacts();
      const existingRelationships = (await getWealthboxInvestorContactRelationships()).reduce(
        function (map, obj) {
          map[obj.contact_id] = obj.customer_id;
          map[obj.customer_id] = obj.contact_id;
          return map;
        },
        {} as Record<string, string>,
      );

      const ourInvestors = new Map<string, InvestorProfile>();
      for (const investor of investors) {
        if (investor.email) {
          ourInvestors.set(investor.email.trim().toLowerCase(), investor);
        }
      }

      const toImport: WealthboxContact[] = [];
      const toExport: InvestorProfile[] = [];
      const toMerge: WealthboxMergeCandidate[] = [];

      for (const contact of wbContacts.contacts as WealthboxContact[]) {
        if (existingRelationships[contact.id.toString()]) {
          continue;
        }
        let matched = false;
        for (const eAddress of contact.email_addresses) {
          const normalizedEmail = eAddress.address.trim().toLowerCase();
          if (ourInvestors.has(normalizedEmail)) {
            const investor = ourInvestors.get(normalizedEmail)!;
            toMerge.push({
              ourId: investor.id,
              wbId: contact.id,
              investor,
            });
            matched = true;
            ourInvestors.delete(normalizedEmail);
            break;
          }
        }
        if (!matched) {
          toImport.push(contact);
        }
      }

      for (const investor of ourInvestors.values()) {
        if (existingRelationships[investor.id]) {
          continue;
        }
        toExport.push(investor);
      }

      if (toExport.length + toImport.length + toMerge.length === 0) {
        // Nothing to sync
        message.info('Everything is already synchronized!');
      } else {
        setSyncState({
          toImport,
          toExport,
          toMerge,
        });
      }
    } catch (e) {
      console.error('Failed to import investors from Wealthbox:', e);
      message.error('Failed to import investors from Wealthbox. Please try again later.');
    }
    setSyncInProgress(false);
  }

  function closeSyncModal() {
    setSyncState(undefined);
    setSyncFailures([]);
    setSubmissionProgress(undefined);
  }

  const syncTableItems = useMemo(() => {
    if (!syncState) {
      return undefined;
    }
    return [
      {
        key: 'import',
        label: 'Import from Wealthbox',
        children: syncState.toImport.map((item) => (
          <SyncItem
            key={item.id.toString()}
            name={item.first_name + ' ' + item.last_name}
            subname={item.email_addresses[0]?.address ?? ''}
            onDelete={() => {
              setSyncState({
                ...syncState,
                toImport: syncState.toImport.filter((c) => c.id !== item.id),
              });
            }}
          ></SyncItem>
        )),
      },
      {
        key: 'export',
        label: 'Export to Wealthbox',
        children: syncState.toExport.map((item) => (
          <SyncItem
            key={item.id}
            name={getClientDisplayName(item)}
            subname={item.email}
            onDelete={() => {
              setSyncState({
                ...syncState,
                toExport: syncState.toExport.filter((c) => c.id !== item.id),
              });
            }}
          ></SyncItem>
        )),
      },
      {
        key: 'merge',
        label: 'Merge matched records',
        children: syncState.toMerge.map((item) => (
          <SyncItem
            key={item.ourId}
            name={getClientDisplayName(item.investor)}
            subname={item.investor.email}
            onDelete={() => {
              setSyncState({
                ...syncState,
                toMerge: syncState.toMerge.filter((c) => c.ourId !== item.ourId),
              });
            }}
          ></SyncItem>
        )),
      },
    ];
  }, [syncState]);

  const [submissionProgress, setSubmissionProgress] = useState<number | undefined>(undefined);

  async function submitChanges() {
    if (!syncState) return;
    setSubmissionProgress(0);
    setSyncFailures([]);

    const total = syncState.toExport.length + syncState.toMerge.length + syncState.toImport.length;
    let finished = 0;
    const failures: WealthboxSyncFailure[] = [];

    if (total === 0) {
      setSubmissionProgress(undefined);
      setSyncState(undefined);
      return;
    }

    for (const item of syncState.toImport) {
      const name = formatWealthboxContactName(item);
      const data: AddInvestorRequest = {
        name,
        email: item.email_addresses.length > 0 ? item.email_addresses[0].address : undefined,
        phone_number: item.phone_numbers.length > 0 ? item.phone_numbers[0].address : undefined,
      };
      try {
        const response = await createInvestorWithArchivedRestore(data);
        await createWealthboxInvestorContactRelationship(response.id, item.id.toString());
      } catch (e) {
        console.error('Failed to create investor', e);
        failures.push({
          item: `${name} (Wealthbox contact ${item.id})`,
          operation: 'Import from Wealthbox',
          reason: getErrorMessage(e),
        });
      }

      finished += 1;
      setSubmissionProgress((finished / total) * 100);
    }

    for (const item of syncState.toMerge) {
      try {
        await createWealthboxInvestorContactRelationship(item.ourId, item.wbId.toString());
      } catch (e) {
        console.error('Failed to create investor relationship', e);
        failures.push({
          item: `${getClientDisplayName(item.investor)} (investor ${item.ourId}, Wealthbox contact ${item.wbId})`,
          operation: 'Merge matched records',
          reason: getErrorMessage(e),
        });
      }

      finished += 1;
      setSubmissionProgress((finished / total) * 100);
    }

    for (const item of syncState.toExport) {
      const normalizedName = (item.name ?? '').trim();
      const nameComponents = normalizedName ? normalizedName.split(/\s+/) : [''];
      let data: CreateContactRequest = {
        first_name: nameComponents[0],
        last_name: nameComponents.length > 1 ? nameComponents[nameComponents.length - 1] : '',
      };
      if (item.email) {
        data = {
          ...data,
          email_addresses: [
            {
              address: item.email,
            },
          ],
        };
      }
      if (item.phone_number) {
        data = {
          ...data,
          phone_numbers: [
            {
              address: item.phone_number,
            },
          ],
        };
      }

      try {
        const response = (await createWealthboxContacts(data)) as WealthboxCreatedContact;
        await createWealthboxInvestorContactRelationship(item.id, response.id.toString());
      } catch (e) {
        console.error('Failed to create contact:', e);
        failures.push({
          item: `${getClientDisplayName(item)} (investor ${item.id})`,
          operation: 'Export to Wealthbox',
          reason: getErrorMessage(e),
        });
      }

      finished += 1;
      setSubmissionProgress((finished / total) * 100);
    }

    setSubmissionProgress(undefined);
    if (failures.length > 0) {
      setSyncFailures(failures);
      message.warning(
        `Wealthbox sync completed with ${formatWealthboxFailureCount(
          failures.length,
        )}. Review the failed records before closing this dialog.`,
      );
      return;
    }

    setSyncState(undefined);
  }

  if (!wealthboxState.status) {
    return null;
  }

  const wealthboxButtonLabel =
    wealthboxState.status === 'authorized'
      ? 'Synchronize'
      : wealthboxState.status === 'error'
        ? 'Wealthbox unavailable'
        : 'Connect Wealthbox';

  return (
    <div>
      <Button
        type="primary"
        className="book-button book-button-primary book-wealthbox-button"
        loading={syncInProgress}
        disabled={wealthboxState.status === 'error'}
        onClick={() => {
          if (wealthboxState.status === 'error') {
            return;
          }
          if (wealthboxState.oauthUrl) {
            window.open(wealthboxState.oauthUrl, '_blank', 'noopener,noreferrer');
          } else if (wealthboxState.status === 'authorized') {
            startImportingInvestors();
          }
        }}
      >
        <span className="book-button-content">
          <WealthboxMarkIcon />
          <span className="book-button-label">{wealthboxButtonLabel}</span>
        </span>
      </Button>

      <Modal
        open={syncState !== undefined}
        closable={true}
        destroyOnClose={true}
        footer={null}
        onCancel={closeSyncModal}
        title="Synchronize Wealthbox Data"
        centered={true}
        width="650px"
      >
        {syncFailures.length > 0 && (
          <Alert
            showIcon
            type="warning"
            message={`Wealthbox sync completed with ${formatWealthboxFailureCount(
              syncFailures.length,
            )}`}
            description={
              <ul className="wealthbox-sync-failures">
                {syncFailures.map((failure) => (
                  <li key={`${failure.operation}-${failure.item}`}>
                    <strong>{failure.operation}:</strong> {failure.item}
                    {failure.reason ? ` (${failure.reason})` : ''}
                  </li>
                ))}
              </ul>
            }
          />
        )}
        <Tabs tabPosition={'left'} items={syncTableItems} />
        {submissionProgress !== undefined && <Progress percent={submissionProgress} />}
        <Flex justify="space-between">
          <div></div>
          <Button
            onClick={() => submitChanges()}
            type="primary"
            loading={submissionProgress !== undefined}
          >
            Confirm
          </Button>
        </Flex>
      </Modal>
    </div>
  );
}

type SyncItemProps = {
  name: string | null;
  subname: string | null | undefined;
  onDelete: () => void;
};

function SyncItem({ name, subname, onDelete }: SyncItemProps) {
  return (
    <Row style={{ margin: '12px', paddingRight: '24px' }}>
      <Col span={23}>
        <Flex vertical>
          <Text>{getClientNameOrFallback(name)}</Text>
          <Text style={{ color: 'gray' }}>{subname ?? ''}</Text>
        </Flex>
      </Col>
      <Col span={1}>
        <Button
          aria-label={`Remove ${getClientNameOrFallback(name)} from sync list`}
          icon={<DeleteOutlined />}
          onClick={onDelete}
        />
      </Col>
    </Row>
  );
}
